import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Typography, Chip, Drawer, IconButton,
    ButtonBase,
    Stack,
} from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MenuIcon from '@mui/icons-material/Menu';
import copy from 'copy-to-clipboard';

import { Box } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

const API_BASE_URL = 'https://b.moovetrax.com:8088';
const BASE_FRONT_URL = 'https://b.moovetrax.com';

const scrollMarginTopXs = '60px';
const scrollMarginTopMd = '10px';

const TEST_VIN = 'JN123456789123456';
const TEST_DEVICE_ID = '1234';
const DEVICE_COMMANDS = [
    { key: 'location', title: 'Return last known location, alive and location time.', url: `${API_BASE_URL}/api/command/oauth/location?vin=${TEST_VIN}&deviceId=${TEST_DEVICE_ID}`, response: { latitude: 34.0522, longitude: -118.2437, time: '2026-09-01T09:16:02.000Z' } },
    { key: 'unlock', title: 'Send unlock command.', url: `${API_BASE_URL}/api/command/oauth/unlock?vin=${TEST_VIN}&deviceId=${TEST_DEVICE_ID}`, response: { message: 'Sent Command.' } },
    { key: 'lock', title: 'Send lock command.', url: `${API_BASE_URL}/api/command/oauth/lock?vin=${TEST_VIN}&deviceId=${TEST_DEVICE_ID}`, response: { message: 'Sent Command.' } },
    { key: 'panic', title: 'Send panic command to honk the horn (5 times).', url: `${API_BASE_URL}/api/command/oauth/panic?vin=${TEST_VIN}&deviceId=${TEST_DEVICE_ID}`, response: { message: 'Sent Command.' } },
    { key: 'kill', title: 'Send kill command.', url: `${API_BASE_URL}/api/command/oauth/kill?vin=${TEST_VIN}&deviceId=${TEST_DEVICE_ID}`, response: { message: 'Sent Command.' } },
    { key: 'unkill', title: 'Send unkill command.', url: `${API_BASE_URL}/api/command/oauth/unkill?vin=${TEST_VIN}&deviceId=${TEST_DEVICE_ID}`, response: { message: 'Sent Command.' } },
    { key: 'speed', title: 'Set speed limit.', url: `${API_BASE_URL}/api/command/oauth/speed?value&vin=${TEST_VIN}&deviceId=${TEST_DEVICE_ID}`, response: { message: 'Sent Command.' } },
    { key: 'mileage', title: 'Read miles.', url: `${API_BASE_URL}/api/command/oauth/mileage?vin=${TEST_VIN}&deviceId=${TEST_DEVICE_ID}`, response: { mileage: 48213.4, distance_unit: 'mile', time: '2026-09-01T09:16:02.000Z' } },
];
const DEVICE_APIS = [
    { key: 'smoke-detection', title: 'Smoking Detection', method: 'POST', url: `${API_BASE_URL}/api/command/oauth/set-device-voltage?vin=${TEST_VIN}&deviceId=${TEST_DEVICE_ID}`, payload: { type: "no_smoke_volt" } },
    { key: 'hood-detection', title: 'Hood Detection', method: 'POST', url: `${API_BASE_URL}/api/command/oauth/set-device-voltage?vin=${TEST_VIN}&deviceId=${TEST_DEVICE_ID}`, payload: { type: "hood_voltage" } },
    { key: 'door-detection', title: 'Door Detection', method: 'POST', url: `${API_BASE_URL}/api/command/oauth/set-device-voltage?vin=${TEST_VIN}&deviceId=${TEST_DEVICE_ID}`, payload: { type: "door_voltage" } },
];
const WEBHOOK_EVENTS = [
    {
        key: 'acc_detection',
        label: 'ACC ON/OFF',
        description: "Fired when a device's ignition (ACC) turns on or off.",
        exampleList: [
            {
                deviceId: 123, vin: "J0123456789012345", acc: true, latitude: 34.0522, longitude: -118.2437, eventTime: '2026-09-01 14:32:10',
            },
            {
                deviceId: 123, vin: "J0123456789012345", acc: false, latitude: 34.0522, longitude: -118.2437, eventTime: '2026-09-01 14:40:02',
            }
        ],
    },
    {
        key: 'mileage',
        label: 'Mileage',
        description: "Periodic odometer snapshot for a device.",
        exampleList: [
            {
                deviceId: 123, vin: "J0123456789012345", mileage: 48213.4, unit: 'mile', eventTime: '2026-09-01 06:00:00',
            }
        ],
    },
    {
        key: 'battery',
        label: 'Battery',
        description: 'Fired when a device battery status changed',
        exampleList: [
            {
                deviceId: 123, vin: "J0123456789012345", mt2v_dc_volt: 11.8, ac_charging_power: 0, dc_charging_power: 21, eventTime: '2026-09-01 09:15:44',
            }
        ],
    },
    {
        key: 'geolocation',
        label: 'Geolocation',
        description: 'Fired on every new position reported by a device.',
        exampleList: [
            {
                deviceId: 123, vin: "J0123456789012345", latitude: 34.0522, longitude: -118.2437, speed: 32, direction: 187, mileage: 48213.4, unit: 'mile', eventTime: '2026-09-01 09:16:02',
            }
        ],
    },
    {
        key: 'user_geofences',
        label: 'User Geofences',
        description: 'Fired when a device enters or leaves a user-defined geofence (status: "enter" | "leave").',
        exampleList: [
            {
                deviceId: 123, vin: "J0123456789012345", fenceId: 45, fenceName: 'Home', status: 'enter', latitude: 34.0522, longitude: -118.2437, eventTime: '2026-09-01 09:20:00',
            }
        ],
    },
    {
        key: 'smoking_detection',
        label: 'Smoking Detection',
        description: 'Fired when smoking is detected or not.',
        exampleList: [
            {
                deviceId: 123, "vin": "J0123456789012345", smoke_status: true, smoke_status_str: 'Smoking Detected', smoke_voltage: 12.8, latitude: 34.0522, longitude: -118.2437, eventTime: '2026-09-01 09:20:00',
            },
            {
                deviceId: 123, "vin": "J0123456789012345", smoke_status: false, smoke_status_str: 'No Smoking Detected', smoke_voltage: 10.8, latitude: 34.0522, longitude: -118.2437, eventTime: '2026-09-01 09:20:00',
            }
        ],
    },
];

const NAV_GROUPS = [
    {
        id: 'oauth',
        label: 'OAuth Integration',
        children: [
            { id: 'oauth-authorization', label: 'Authorization URL' },
            { id: 'oauth-token', label: 'Token URL' },
            { id: 'oauth-refresh-token', label: 'Refresh Token URL' },
            { id: 'oauth-get-connected-devices', label: 'Get Connected Devices' },
            { id: 'oauth-device-disconnect', label: 'Device Disconnect' },
        ],
    },
    {
        id: 'device-commands',
        label: 'Device Commands',
        children: DEVICE_COMMANDS.map((c) => ({ id: `device-command-${c.key}`, label: c.title })),
    },
    {
        id: 'detection-api',
        label: 'Detection API',
        children: DEVICE_APIS.map((c) => ({ id: `detection-api-${c.key}`, label: c.title })),
    },
];

const CodeBox = ({ children }) => (
    <Box
        sx={{
            backgroundColor: '#2b2b2b',
            color: '#e0e0e0',
            borderRadius: '4px',
            p: 2,
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
        }}
    >
        {children}
    </Box>
);

const CopyButton = ({ value }) => (
    <Box
        component="button"
        type="button"
        onClick={() => copy(value)}
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            border: '1px solid #d3d9e6',
            borderRadius: '4px',
            background: '#fff',
            color: '#377dff',
            fontSize: '0.9rem',
            fontWeight: '600',
            px: 1,
            py: 0.5,
            cursor: 'pointer',
            mt: 1,
            '&:hover': { background: '#f8fafd' },
        }}
    >
        <ContentCopyIcon style={{ fontSize: '1rem' }} />
        Copy
    </Box>
);

const NavSectionLabel = ({ children }) => (
    <Typography
        variant="caption"
        sx={{
            display: 'block', fontWeight: 700, color: '#8c98a9', letterSpacing: '0.06em', px: 2, mt: 3, mb: 1,
        }}
    >
        {children}
    </Typography>
);

const SectionHeading = ({ id, children }) => {
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    return (
        <Typography id={id} variant="h4" sx={{ fontWeight: 700, mt: 6, mb: 2, scrollMarginTop: isTablet ? scrollMarginTopXs : scrollMarginTopMd }}>
            {children}
        </Typography>
    )
};

const SubHeading = ({ id, children }) => {
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    return (
        <Typography id={id} variant="h6" sx={{ fontWeight: 700, mb: 1, scrollMarginTop: isTablet ? scrollMarginTopXs : scrollMarginTopMd }}>
            {children}
        </Typography>
    )
};

const DocsSidebarContent = (props) => {
    const { activeId, expandedGroups, onClickGroup, onNavigate } = props;
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    return (
        <>
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', px: 2, mb: 1 }}>
                Moovetrax API
            </Typography>

            <Box
                onClick={() => onNavigate('overview')}
                sx={{
                    px: 2, py: 1, cursor: 'pointer', fontSize: '0.9rem',
                    background: activeId === 'overview' ? '#dce8fb' : 'transparent',
                    fontWeight: activeId === 'overview' ? 700 : 400,
                    '&:hover': { background: '#eef2fa' },
                }}
            >
                Overview
            </Box>

            <NavSectionLabel>ENDPOINTS</NavSectionLabel>
            {NAV_GROUPS.map((group) => (
                <Box key={group.id}>
                    <Box
                        onClick={() => onClickGroup(group.id)}
                        sx={{
                            px: 2, py: 1, cursor: 'pointer', fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            '&:hover': { background: '#eef2fa' },
                        }}
                    >
                        {group.label}
                        <ExpandMoreIcon
                            fontSize="small"
                            sx={{ transform: expandedGroups[group.id] ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s', color: '#8c98a9' }}
                        />
                    </Box>
                    {expandedGroups[group.id] && group.children.map((child) => (
                        <Box
                            key={child.id}
                            onClick={() => onNavigate(child.id)}
                            sx={{
                                pl: 4, pr: 2, py: 0.75, cursor: 'pointer', fontSize: '0.85rem',
                                background: activeId === child.id ? '#dce8fb' : 'transparent',
                                fontWeight: activeId === child.id ? 700 : 400,
                                '&:hover': { background: '#eef2fa' },
                            }}
                        >
                            {child.label}
                        </Box>
                    ))}
                </Box>
            ))}

            <NavSectionLabel>WEBHOOKS</NavSectionLabel>
            {WEBHOOK_EVENTS.map((event) => (
                <Box
                    key={event.key}
                    onClick={() => onNavigate(`webhook-${event.key}`)}
                    sx={{
                        px: 2, py: 0.75, cursor: 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 1,
                        background: activeId === `webhook-${event.key}` ? '#dce8fb' : 'transparent',
                        fontWeight: activeId === `webhook-${event.key}` ? 700 : 400,
                        '&:hover': { background: '#eef2fa' },
                    }}
                >
                    <MailOutlineIcon sx={{ fontSize: '1rem', color: '#377dff' }} />
                    <Box sx={{ flex: 1, pl: 0.5 }}>{event.label}</Box>
                    <Typography variant="caption" sx={{ color: '#377dff', fontWeight: 700 }}>POST</Typography>
                </Box>
            ))}
        </>
    )
}

export default function ApiDocSite() {
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const [activeId, setActiveId] = useState('overview');
    const [expandedGroups, setExpandedGroups] = useState({});
    const [mobileNavOpen, setMobileNavOpen] = useState(false);

    // Supports deep links like /documentation#oauth or #webhooks (e.g. the old /oauth-documentation redirect).
    useEffect(() => {
        const hashId = window.location.hash ? window.location.hash.slice(1) : '';
        if (!hashId) {
            return;
        }
        const group = NAV_GROUPS.find((g) => g.id === hashId || g.children.some((c) => c.id === hashId));
        if (group) {
            setExpandedGroups((prev) => ({ ...prev, [group.id]: true }));
        }
        setTimeout(() => scrollToId(hashId), 0);
    }, []);

    const scrollToId = (id) => {
        setActiveId(id);
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const onClickGroup = (groupId) => {
        setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
    };

    const onNavigate = (id) => {
        scrollToId(id);
        setMobileNavOpen(false);
    };

    const authorizeUrlExample = `${BASE_FRONT_URL}/oauth/authorize?response_type=code&client_id={client_id}&redirect_uri={redirect_url}`;
    const tokenUrlExample = `${API_BASE_URL}/oauth/token`;
    const tokenBodyExample = JSON.stringify(
        {
            client_id: '{client_id}',
            client_secret: '{client_secret}',
            grant_type: 'authorization_code',
            code: '{authorizationCode}',
            redirect_uri: '{redirect_url}',
        },
        null,
        2,
    );

    const refreshTokenBodyExample = JSON.stringify(
        {
            client_id: '{client_id}',
            client_secret: '{client_secret}',
            grant_type: 'refresh_token',
            refresh_token: '{refresh_token}',
        },
        null,
        2,
    );

    const deviceListUrlExample = `${API_BASE_URL}/oauth/device/list`;

    const deviceDisconnectUrlExample = `${API_BASE_URL}/oauth/device/disconnect`;
    const deviceDisconnectBodyExample = JSON.stringify(
        {
            deviceId: '{deviceId}',
        },
        null,
        2,
    );

    const authenticatedHeaderExample = JSON.stringify(
        {
            'Content-Type': 'application/json',
            'Authorization': `Bearer {access_token}`
        },
        null,
        2,
    );

    const commandHeaderExample = JSON.stringify(
        {
            'Content-Type': 'application/json',
            'Authorization': `Bearer 1234567890`
        },
        null,
        2,
    );

    return (
        <React.Fragment>
            <main id="content" role="main">
                {/* Mobile nav bar */}
                <Box
                    sx={{
                        display: { xs: 'flex', md: 'none !important' },
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1.5,
                        borderTop: '1px solid #e7eaf3',
                        borderBottom: '1px solid #e7eaf3',
                        position: 'sticky',
                        top: 0,
                        background: '#fff',
                        zIndex: 10,
                    }}
                >
                    <IconButton onClick={() => setMobileNavOpen(true)} size="small" aria-label="Open documentation menu">
                        <MenuIcon />
                    </IconButton>
                    {/* <Typography sx={{ fontWeight: 700 }}>Menu</Typography> */}
                </Box>

                <Drawer
                    anchor="left"
                    open={mobileNavOpen}
                    onClose={() => setMobileNavOpen(false)}
                    ModalProps={{ keepMounted: true }}
                    sx={{ display: { xs: 'block', md: 'none' } }}
                    PaperProps={{ sx: { width: 280 } }}
                >
                    <Box sx={{ py: 2 }}>
                        <DocsSidebarContent
                            activeId={activeId}
                            expandedGroups={expandedGroups}
                            onClickGroup={onClickGroup}
                            onNavigate={onNavigate}
                        />
                    </Box>
                </Drawer>

                <Box sx={{ background: '#fff', display: 'flex', maxWidth: 1400, mx: 'auto' }}>
                    {/* Sidebar (desktop) */}
                    <Box
                        component="aside"
                        sx={{
                            width: 280,
                            flexShrink: 0,
                            borderRight: '1px solid #e7eaf3',
                            background: '#f8fafd',
                            position: 'sticky',
                            top: 0,
                            alignSelf: 'flex-start',
                            maxHeight: '100vh',
                            overflowY: 'auto',
                            py: 2,
                            display: { xs: 'none', md: 'block' },
                        }}
                    >
                        <DocsSidebarContent
                            activeId={activeId}
                            expandedGroups={expandedGroups}
                            onClickGroup={onClickGroup}
                            onNavigate={onNavigate}
                        />
                    </Box>

                    {/* Content */}
                    <Box component="section" sx={{ flex: 1, minWidth: 0, px: 4, py: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 8, mb: 2 }}>
                            <Typography variant="h4" sx={{ fontWeight: 800 }}>Moovetrax API</Typography>
                        </Box>

                        <Box sx={{ borderRadius: '6px', overflow: 'hidden', border: '1px solid #1e2a3a', mb: 5 }}>
                            <Box sx={{ background: '#1e2a3a', color: '#fff', px: 2, py: 1, fontWeight: 700, fontSize: '0.85rem' }}>
                                API Base URL
                            </Box>
                            <Box sx={{ background: '#28374a', color: '#fff', px: 2, py: 1.5, fontSize: '0.85rem' }}>
                                {API_BASE_URL}
                            </Box>
                        </Box>

                        <SectionHeading id="overview">Getting Started</SectionHeading>

                        <Typography sx={{ mb: 1 }}>
                            For an OAuth application, contact
                            {' '}
                            <a href="mailto:support@moovetrax.com">support@moovetrax.com</a>
                            .
                        </Typography>

                        <SubHeading id="navigation">Navigation</SubHeading>
                        <Box component="ol" sx={{ mb: 2 }}>
                            <li>
                                <Typography
                                    component="a"
                                    onClick={() => scrollToId('oauth')}
                                    sx={{ color: '#377dff', cursor: 'pointer' }}
                                >
                                    OAuth Integration
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    component="a"
                                    onClick={() => scrollToId('device-commands')}
                                    sx={{ color: '#377dff', cursor: 'pointer' }}
                                >
                                    Device Commands
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    component="a"
                                    onClick={() => scrollToId('detection-api-smoke-detection')}
                                    sx={{ color: '#377dff', cursor: 'pointer' }}
                                >
                                    Detection API
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    component="a"
                                    onClick={() => scrollToId('webhooks')}
                                    sx={{ color: '#377dff', cursor: 'pointer' }}
                                >
                                    Webhooks
                                </Typography>
                            </li>
                        </Box>

                        <Box>
                            {/* OAuth */}
                            <SectionHeading id="oauth">OAuth Integration</SectionHeading>
                            <Typography sx={{ mb: 3 }}>
                                Please setup a <b>redirect_url</b> and contact support to get a <b>client_id</b> and a <b>client_secret</b>
                            </Typography>

                            <Box sx={{ border: '1px solid #e7eaf3', borderRadius: '6px', p: 2, mb: 2 }}>
                                <SubHeading id="oauth-authorization">Authorization URL</SubHeading>
                                <Box sx={{ mb: 0.5 }}>
                                    <CodeBox>{authorizeUrlExample}</CodeBox>
                                </Box>
                                <CopyButton value={authorizeUrlExample} />

                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mt: 2 }}>REQUIRED:</Typography>
                                <Box component="ul" sx={{ mt: 0.5, mb: 1 }}>
                                    <li><Typography variant="body2">response_type</Typography></li>
                                    <li><Typography variant="body2">client_id</Typography></li>
                                    <li><Typography variant="body2">redirect_uri</Typography></li>
                                </Box>
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mt: 1 }}>OPTIONAL:</Typography>
                                <Box component="ul" sx={{ mt: 0.5, mb: 2 }}>
                                    <li><Typography variant="body2">state</Typography></li>
                                </Box>
                            </Box>

                            <Box sx={{ border: '1px solid #e7eaf3', borderRadius: '6px', p: 2, mb: 2 }}>
                                <SubHeading id="oauth-token">Token URL</SubHeading>
                                <Box sx={{ mb: 0.5 }}>
                                    <CodeBox>{`Method: POST\n\n${tokenUrlExample}\n\nBody:\n\n${tokenBodyExample}`}</CodeBox>
                                </Box>
                                <CopyButton value={tokenUrlExample} />

                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mt: 2 }}>REQUIRED:</Typography>
                                <Box component="ul" sx={{ mt: 0.5, mb: 2 }}>
                                    <li><Typography variant="body2">client_id</Typography></li>
                                    <li><Typography variant="body2">client_secret</Typography></li>
                                    <li><Typography variant="body2">grant_type</Typography></li>
                                    <li><Typography variant="body2">code</Typography></li>
                                    <li><Typography variant="body2">redirect_uri</Typography></li>
                                </Box>
                            </Box>

                            <Box sx={{ border: '1px solid #e7eaf3', borderRadius: '6px', p: 2, mb: 2 }}>
                                <SubHeading id="oauth-refresh-token">Refresh Token URL</SubHeading>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    Access tokens expire; use the refresh token from the Token URL response to get a new
                                    pair before that happens. The old refresh token is revoked as soon as a new pair is
                                    issued, so always store the latest one.
                                </Typography>
                                <Box sx={{ mb: 0.5 }}>
                                    <CodeBox>{`Method: POST\n\n${tokenUrlExample}\n\nBody:\n\n${refreshTokenBodyExample}`}</CodeBox>
                                </Box>
                                <CopyButton value={tokenUrlExample} />

                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mt: 2 }}>REQUIRED:</Typography>
                                <Box component="ul" sx={{ mt: 0.5, mb: 2 }}>
                                    <li><Typography variant="body2">client_id</Typography></li>
                                    <li><Typography variant="body2">client_secret</Typography></li>
                                    <li><Typography variant="body2">grant_type</Typography></li>
                                    <li><Typography variant="body2">refresh_token</Typography></li>
                                </Box>
                            </Box>

                            <Box sx={{ border: '1px solid #e7eaf3', borderRadius: '6px', p: 2, mb: 2 }}>
                                <SubHeading id="oauth-get-connected-devices">Get Connected Devices</SubHeading>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    Get all devices connected to the access token.
                                </Typography>
                                <Box sx={{ mb: 0.5 }}>
                                    <CodeBox>{`Method: GET\n\n${deviceListUrlExample}\n\nHeader:\n\n${authenticatedHeaderExample}`}</CodeBox>
                                </Box>
                                <CopyButton value={deviceListUrlExample} />

                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mt: 2 }}>REQUIRED:</Typography>
                                <Box component="ul" sx={{ mt: 0.5, mb: 2 }}>
                                    <li><Typography variant="body2">access_token</Typography></li>
                                </Box>
                            </Box>

                            <Box sx={{ border: '1px solid #e7eaf3', borderRadius: '6px', p: 2, mb: 2 }}>
                                <SubHeading id="oauth-device-disconnect">Device Disconnect</SubHeading>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    Revoke a single device from an access token&apos;s grant without revoking the whole
                                    token. The device is removed from the token&apos;s device list; other devices
                                    authorized under the same token are unaffected.
                                </Typography>
                                <Box sx={{ mb: 0.5 }}>
                                    <CodeBox>{`Method: DELETE\n\n${deviceDisconnectUrlExample}\n\nHeader:\n\n${authenticatedHeaderExample}\n\nBody:\n\n${deviceDisconnectBodyExample}`}</CodeBox>
                                </Box>
                                <CopyButton value={deviceDisconnectUrlExample} />

                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mt: 2 }}>REQUIRED:</Typography>
                                <Box component="ul" sx={{ mt: 0.5, mb: 2 }}>
                                    <li><Typography variant="body2">access_token</Typography></li>
                                    <li><Typography variant="body2">deviceId</Typography></li>
                                </Box>
                            </Box>
                        </Box>

                        <Box>
                            {/* Device Commands */}
                            <SectionHeading id="device-commands">Device Commands</SectionHeading>
                            <Typography sx={{ mb: 3 }}>
                                In this example the vin is {' '} <b>{TEST_VIN}</b> and the deviceId is {' '} <b>{TEST_DEVICE_ID}</b> and the access_token is {' '} <b>1234567890</b>
                                {' '}
                                (found in the response of <ButtonBase disableRipple={true} onClick={() => scrollToId('oauth-token')}><b style={{ color: '#377dff', cursor: 'pointer' }}>Token URL</b></ButtonBase>)
                            </Typography>

                            {DEVICE_COMMANDS.map((command) => {
                                const responseBody = typeof command.response === 'string' ? `"${command.response}"` : JSON.stringify(command.response, null, 2);
                                return (
                                    <Box
                                        key={command.key}
                                        id={`device-command-${command.key}`}
                                        sx={{
                                            scrollMarginTop: isTablet ? scrollMarginTopXs : scrollMarginTopMd, border: '1px solid #e7eaf3', borderRadius: '6px', p: 2, mb: 2,
                                        }}
                                    >
                                        <Typography sx={{ fontWeight: 700, mb: 1 }}>{command.title}</Typography>
                                        <Box sx={{ mb: 0.5 }}>
                                            <CodeBox>{`Method: GET\n\n${command.url}\n\nHeader:\n\n${commandHeaderExample}`}</CodeBox>
                                        </Box>
                                        <CopyButton value={command.url} />

                                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mt: 2 }}>RESPONSE (200):</Typography>
                                        <Box sx={{ mt: 0.5 }}>
                                            <CodeBox>{responseBody}</CodeBox>
                                        </Box>
                                    </Box>
                                );
                            })}

                            {/* <Box sx={{ border: '1px solid #e7eaf3', borderRadius: '6px', p: 2, mb: 2, backgroundColor: '#f7f8fb' }}>
                                <Typography sx={{ fontWeight: 700, mb: 1 }}>Queued Commands &amp; Offline Devices</Typography>
                                <Typography variant="body2">
                                    <code>location</code> and <code>mileage</code> read live/cached data and respond immediately.
                                    Every other command (<code>unlock</code>, <code>lock</code>, <code>panic</code>, <code>kill</code>,
                                    <code>unkill</code>, <code>speed</code>) is queued for delivery the next time the device checks in
                                    &mdash; the <code>200 {`{"message": "Sent Command."}`}</code> response only confirms the command was queued, not that
                                    the device has executed it yet. If the device is currently offline the response is identical; the
                                    command simply waits in the queue (internal status <code>"waiting"</code>) until the device
                                    reconnects and picks it up.
                                </Typography>
                            </Box> */}

                            <Box sx={{ border: '1px solid #e7eaf3', borderRadius: '6px', p: 2, mb: 2 }}>
                                <Typography sx={{ fontWeight: 700, mb: 1 }}>Errors</Typography>

                                <Typography variant="body2" sx={{ mb: 0.5 }}>No device matches the vin:</Typography>
                                <CodeBox>{`Status: 400\n\n${JSON.stringify({ error: "No device found" }, null, 2)}`}</CodeBox>

                                <Typography variant="body2" sx={{ mt: 2, mb: 0.5 }}>The vin is not one of the devices granted to this access token:</Typography>
                                <CodeBox>{`Status: 500\n\n${JSON.stringify({ error: "Invalid permission", deviceId: 123, oauth_device_ids: [123, 456] }, null, 2)}`}</CodeBox>

                                <Typography variant="body2" sx={{ mt: 2, mb: 0.5 }}>Unknown command type:</Typography>
                                <CodeBox>{`Status: 500\n\n${JSON.stringify({ error: "Invalid command" }, null, 2)}`}</CodeBox>

                                <Typography variant="body2" sx={{ mt: 2, mb: 0.5 }}>
                                    Application is registered read-only (Admin &gt; OAuth Apps &gt; Read data only) and the command is
                                    not Location:
                                </Typography>
                                <CodeBox>{`Status: 403\n\n${JSON.stringify({ error: "This application is authorized for read-only access and cannot send commands" }, null, 2)}`}</CodeBox>
                            </Box>
                        </Box>

                        <Box>
                            {/* Detection API */}
                            <SectionHeading id="detection-api">Detection API</SectionHeading>
                            <Typography sx={{ mb: 3 }}>
                                Devices equipped with a sensor tracker based on a configurable voltage threshold on the sensor.
                            </Typography>
                            {DEVICE_APIS.map((command) => (
                                <Box
                                    key={command.key}
                                    id={`detection-api-${command.key}`}
                                    sx={{
                                        scrollMarginTop: isTablet ? scrollMarginTopXs : scrollMarginTopMd, border: '1px solid #e7eaf3', borderRadius: '6px', p: 2, mb: 2,
                                    }}
                                >
                                    <Typography sx={{ fontWeight: 700, mb: 1 }}>{command.title}</Typography>
                                    <Box sx={{ mb: 0.5 }}>
                                        <CodeBox>{`Method: ${command.method}\n\n${command.url}\n\nHeader:\n\n${commandHeaderExample}\n\nPayload:\n\n${JSON.stringify(command.payload, null, 2)}`}</CodeBox>
                                    </Box>
                                    <CopyButton value={command.url} />
                                </Box>
                            ))}
                        </Box>

                        <Box>
                            {/* Webhooks */}
                            <SectionHeading id="webhooks">Webhooks</SectionHeading>
                            <Typography sx={{ mb: 1 }}>
                                Moovetrax pushes real-time events to your application via signed HTTP webhooks, configured
                                per OAuth application from the admin panel.
                            </Typography>
                            <Typography sx={{ mb: 3 }}>
                                Each request is a
                                {' '}
                                <b>POST</b>
                                {' '}
                                with an
                                {' '}
                                <code>X-Moovetrax-Event</code>
                                {' '}
                                header naming the event, and an
                                {' '}
                                <code>X-Moovetrax-Signature: sha256=&lt;hex&gt;</code>
                                {' '}
                                header &mdash; an HMAC-SHA256 signature of the raw JSON body, computed using your webhook&apos;s
                                key as the secret.
                            </Typography>


                            <Box sx={{ border: '1px solid #e7eaf3', borderRadius: '6px', p: 2, mb: 3 }}>
                                <Typography sx={{ fontWeight: 700, mb: 1 }}>Verifying The Signature</Typography>
                                <CodeBox>{`Headers:

Content-Type: application/json
X-Moovetrax-Event: geolocation
X-Moovetrax-Signature: sha256=5f2b1c9e4a7d3f0b8c6e1a9d2f4b7c0e3a6d9f1b4c7e0a3d6f9b2c5e8a1d4f7b

Verify (Node.js):

const crypto = require('crypto');
const expected = crypto.createHmac('sha256', webhookKey).update(rawRequestBody).digest('hex');
const valid = expected === signatureHeader.replace('sha256=', '');`}</CodeBox>
                            </Box>
                            {WEBHOOK_EVENTS.map((event) => (
                                <Box key={event.key} id={`webhook-${event.key}`} sx={{ border: '1px solid #e7eaf3', borderRadius: '6px', p: 2, mb: 2, scrollMarginTop: isTablet ? scrollMarginTopXs : scrollMarginTopMd }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 8, mb: 0.5 }}>
                                        <MailOutlineIcon sx={{ fontSize: '1.1rem', color: '#377dff' }} />
                                        <Typography sx={{ fontWeight: 700 }}>{event.label}</Typography>
                                        <Typography variant="caption" sx={{ color: '#377dff', fontWeight: 700 }}>POST</Typography>
                                        {/* <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#8c98a9' }}>
                                        {event.key}
                                    </Typography> */}
                                    </Box>
                                    <Typography variant="body2" sx={{ mb: 1 }}>{event.description}</Typography>
                                    <Stack direction={`column`} spacing={1}>
                                        {
                                            event.exampleList.map((example) => {
                                                return (
                                                    <CodeBox>
                                                        {JSON.stringify({ event: event.key, created_at: '2026-09-01 09:20:00', data: example }, null, 2)}
                                                    </CodeBox>
                                                )
                                            })
                                        }
                                    </Stack>
                                </Box>
                            ))}
                        </Box>

                        <Box>
                            <Link to="/v1-documentation" target="_blank">
                                <Typography sx={{ fontWeight: 700, textDecoration: 'underline' }}>Api Version 1</Typography>
                            </Link>
                        </Box>
                    </Box>
                </Box>
            </main>
        </React.Fragment>
    );
}
